// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
	useState,
	useRef,
	useMemo,
	useEffect,
	useCallback,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { WalletCard } from '../wallet/WalletCard';
import { TransactionHistory } from '../wallet/TransactionHistory';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { useAuth } from '../../hooks/useAuth';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import {
	ArrowLeft,
	User,
	Calendar,
	Wallet,
	Copy,
	Settings,
	Trophy,
	ExternalLink,
	CheckCircle,
	RefreshCw,
	Hammer,
	Gamepad2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SquarePenIcon } from '@/components/ui/icons/square-pen-icon';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import {
	WATCHER_ACCESS_PASS_CONTRACT_ADDRESSES,
	WATCHER_BUILDER_CONTRACT_ADDRESS,
	WATCHER_GAME_ID_CONTRACT_ADDRESS,
	WATCHER_PLAYER_CONTRACT_ADDRESS,
} from '@/lib/watcher-contracts';
import {
	getAccessPassImageCandidates as getAccessPassImageCandidatesFromAsset,
	getGameIdImageCandidates as getGameIdImageCandidatesFromAsset,
} from '@/lib/profile-nft-assets';
import { Loader } from '@/components/ui/loader';

// Import affiliate components
import { AffiliateStats } from '../AffiliateStats';

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';

// Token address from env var for balance display
const tokenAddress = import.meta.env.VITE_PREDICT_TOKEN_ADDRESS;
const WATCHER_HQ_MEMBER_PROFILE_URL =
	'https://watchersociety.com/members/profile';
const NFT_READ_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const NFT_READ_CACHE_PREFIX = 'prometheus-admin:nft-read:v1:';

type BlockchainNftReadResult = {
	success: boolean;
	nfts: GameIdNftWithMetadata[];
	totalNfts: number;
	error?: string;
};

const inFlightNftReads = new Map<string, Promise<BlockchainNftReadResult>>();

function getNftReadCacheKey(walletAddress: string, contractAddress: string) {
	return `${walletAddress.trim().toLowerCase()}:${contractAddress.trim().toLowerCase()}`;
}

function readCachedNftResult(cacheKey: string): BlockchainNftReadResult | null {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(`${NFT_READ_CACHE_PREFIX}${cacheKey}`);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as {
			expiresAt?: number;
			value?: BlockchainNftReadResult;
		};
		if (!parsed.expiresAt || parsed.expiresAt <= Date.now() || !parsed.value) {
			window.localStorage.removeItem(`${NFT_READ_CACHE_PREFIX}${cacheKey}`);
			return null;
		}
		return parsed.value;
	} catch {
		return null;
	}
}

function writeCachedNftResult(cacheKey: string, value: BlockchainNftReadResult) {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(
			`${NFT_READ_CACHE_PREFIX}${cacheKey}`,
			JSON.stringify({
				expiresAt: Date.now() + NFT_READ_CACHE_TTL_MS,
				value,
			})
		);
	} catch {
		// Browser storage can be unavailable in hardened/private sessions.
	}
}

// Type for user data that includes gameId
type UserWithGameId = {
	_id: Id<'users'>;
	email: string;
	name: string;
	role?: string;
	createdAt?: number;
	walletAddress?: string;
	gameId?: string;
};

// Type for Game ID NFT metadata
interface GameIdNftMetadata {
	name: string;
	description: string;
	image: string;
	external_url?: string;
	attributes: Array<{
		trait_type: string;
		value: string | number | boolean;
		display_type?: string;
	}>;
	imageGatewayUrl?: string;
	imageIPFSHash?: string;
	image_url?: string;
	uri?: string;
	contract_address?: string;
}

// Type for Game ID NFT with metadata
interface GameIdNftWithMetadata {
	id: string;
	tokenURI: string;
	metadata: GameIdNftMetadata;
	owner: string;
	type: string;
	tokenAddress: string;
	chainId: number;
	ipfsData?: {
		image?: {
			ipfsHash: string;
			gatewayUrl: string;
			originalUrl: string;
		};
	} | null;
}

// Type for Access Pass NFT metadata (separate from Game ID)
interface AccessPassNftMetadata {
	name: string;
	description: string;
	image: string;
	external_url?: string;
	attributes: Array<{
		trait_type: string;
		value: string | number | boolean;
		display_type?: string;
	}>;
	imageGatewayUrl?: string;
	imageIPFSHash?: string;
	image_url?: string;
	uri?: string;
	contract_address?: string;
}

// Type for Access Pass NFT with metadata (separate from Game ID)
interface AccessPassNftWithMetadata {
	id: string;
	tokenURI: string;
	metadata: AccessPassNftMetadata;
	owner: string;
	type: string;
	tokenAddress: string;
	chainId: number;
	ipfsData?: {
		image?: {
			ipfsHash: string;
			gatewayUrl: string;
			originalUrl: string;
		};
	} | null;
}

const DEFAULT_GAME_ID_IMAGE =
	// no-public-ipfs-fallback: owner-authored Game ID background art; every real
	// metadata image resolves through our own gateways before this is used
	'https://watchers.sirv.com/watchers/nft/gameid-bg.jpg';
const DEFAULT_ACCESS_PASS_IMAGE = DEFAULT_GAME_ID_IMAGE;
const GAME_ID_IMAGE_TIMEOUT_MS = 8000;
const ACCESS_PASS_IMAGE_TIMEOUT_MS = GAME_ID_IMAGE_TIMEOUT_MS;
const getGameIdImageCandidates = (nft: GameIdNftWithMetadata) =>
	getGameIdImageCandidatesFromAsset(
		nft,
		DEFAULT_GAME_ID_IMAGE
	);

const getAccessPassImageCandidates = (nft: AccessPassNftWithMetadata) =>
	getAccessPassImageCandidatesFromAsset(
		nft,
		DEFAULT_ACCESS_PASS_IMAGE
	);

const getGameIdNftKey = (nft: GameIdNftWithMetadata) =>
	`${nft.tokenAddress || 'pending'}:${nft.id}`;

const GameIdLoadingCard: React.FC<{ gameId?: string }> = ({ gameId }) => (
	<div className='mt-4 rounded-2xl overflow-hidden bg-black/60 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(236,72,153,0.2)]'>
		<div className='relative aspect-square bg-black'>
			<img
				src={DEFAULT_GAME_ID_IMAGE}
				alt=''
				aria-hidden='true'
				className='absolute inset-0 h-full w-full object-cover'
			/>
			<div className='absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]'>
				<Loader className='scale-50' message='' />
			</div>
		</div>
		<div className='p-4 text-center'>
			<h4 className='text-lg font-semibold text-white'>
				{gameId ? `${gameId} Game ID` : 'Loading Game ID'}
			</h4>
			<p className='text-sm text-pink-200'>
				Loading Game ID NFT data...
			</p>
		</div>
	</div>
);

const GameIdNftImage: React.FC<{ nft: GameIdNftWithMetadata }> = ({ nft }) => {
	const candidates = useMemo(() => getGameIdImageCandidates(nft), [nft]);
	const candidateKey = candidates.join('\n');
	const [candidateIndex, setCandidateIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [hasLoadedImage, setHasLoadedImage] = useState(false);
	const currentSrc = candidates[candidateIndex] || DEFAULT_GAME_ID_IMAGE;

	useEffect(() => {
		setCandidateIndex(0);
		setIsLoading(true);
		setHasLoadedImage(false);
	}, [candidateKey]);

	useEffect(() => {
		if (!isLoading) return;
		const timeout = window.setTimeout(() => {
			if (candidateIndex < candidates.length - 1) {
				setCandidateIndex((index) => index + 1);
				setHasLoadedImage(false);
				setIsLoading(true);
				return;
			}
			setIsLoading(false);
		}, GAME_ID_IMAGE_TIMEOUT_MS);
		return () => window.clearTimeout(timeout);
	}, [candidateIndex, candidateKey, candidates.length, isLoading]);

	const tryNextCandidate = () => {
		if (candidateIndex < candidates.length - 1) {
			setCandidateIndex((index) => index + 1);
			setHasLoadedImage(false);
			setIsLoading(true);
			return;
		}
		setIsLoading(false);
		setHasLoadedImage(false);
	};

	return (
		<div className='relative h-full w-full overflow-hidden bg-black group'>
			<img
				src={DEFAULT_GAME_ID_IMAGE}
				alt=''
				aria-hidden='true'
				className='absolute inset-0 h-full w-full object-cover'
			/>
			<img
				key={currentSrc}
				src={currentSrc}
				alt={nft.metadata.name || `Game ID NFT ${nft.id}`}
				loading='eager'
				decoding='async'
				className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 group-hover:scale-105 ${hasLoadedImage ? 'opacity-100' : 'opacity-0'}`}
				onLoad={() => {
					setHasLoadedImage(true);
					setIsLoading(false);
				}}
				onError={tryNextCandidate}
			/>
			{isLoading && (
				<div className='absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]'>
					<Loader className='scale-50' message='' />
				</div>
			)}
			{!isLoading && !hasLoadedImage && (
				<div className='absolute inset-0 flex items-center justify-center bg-black/30'>
					<Trophy className='h-8 w-8 text-pink-400' />
				</div>
			)}
		</div>
	);
};

const GameIdNftCarouselImages: React.FC<{
	nfts: GameIdNftWithMetadata[];
	currentIndex: number;
	onPrev: () => void;
	onNext: () => void;
	onSelect: (index: number) => void;
}> = ({ nfts, currentIndex, onPrev, onNext, onSelect }) => (
	<div className='relative mb-4'>
		<div className='relative aspect-square rounded-2xl overflow-hidden bg-black border border-white/20 shadow-[0_0_15px_rgba(236,72,153,0.2)]'>
			{nfts.map((nft, index) => (
				<div
					key={getGameIdNftKey(nft)}
					aria-hidden={index !== currentIndex}
					className={`absolute inset-0 transition-opacity duration-300 ${
						index === currentIndex
							? 'opacity-100'
							: 'opacity-0 pointer-events-none'
					}`}
				>
					<GameIdNftImage nft={nft} />
				</div>
			))}
		</div>

		{nfts.length > 1 && (
			<>
				<button
					type='button'
					aria-label='Previous Game ID'
					onClick={onPrev}
					className='absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/80 p-2 text-white transition-colors hover:bg-black/90 shadow-[0_0_20px_rgba(236,72,153,0.6)] hover:shadow-[0_0_25px_rgba(236,72,153,0.8)]'
				>
					<ArrowLeft className='h-5 w-5' />
				</button>
				<button
					type='button'
					aria-label='Next Game ID'
					onClick={onNext}
					className='absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/80 p-2 text-white transition-colors hover:bg-black/90 shadow-[0_0_20px_rgba(236,72,153,0.6)] hover:shadow-[0_0_25px_rgba(236,72,153,0.8)]'
				>
					<ArrowLeft className='h-5 w-5 rotate-180' />
				</button>
				<div className='absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1'>
					{nfts.map((nft, index) => (
						<button
							key={getGameIdNftKey(nft)}
							type='button'
							aria-label={`Show Game ID ${index + 1}`}
							onClick={() => onSelect(index)}
							className={`h-2 w-2 rounded-full transition-colors ${
								index === currentIndex
									? 'bg-pink-400'
									: 'bg-white/40 hover:bg-white/60'
							}`}
						/>
					))}
				</div>
			</>
		)}
	</div>
);

export const AccessPassNftImage: React.FC<{
	nft: AccessPassNftWithMetadata;
	objectFit?: 'cover' | 'contain';
	imageClassName?: string;
	fallbackIconClassName?: string;
}> = ({
	nft,
	objectFit = 'cover',
	imageClassName = '',
	fallbackIconClassName = 'h-8 w-8',
}) => {
	const candidates = useMemo(() => getAccessPassImageCandidates(nft), [nft]);
	const candidateKey = candidates.join('\n');
	const [candidateIndex, setCandidateIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [hasLoadedImage, setHasLoadedImage] = useState(false);
	const currentSrc = candidates[candidateIndex] || DEFAULT_ACCESS_PASS_IMAGE;

	useEffect(() => {
		setCandidateIndex(0);
		setIsLoading(true);
		setHasLoadedImage(false);
	}, [candidateKey]);

	useEffect(() => {
		if (!isLoading) return;
		const timeout = window.setTimeout(() => {
			if (candidateIndex < candidates.length - 1) {
				setCandidateIndex((index) => index + 1);
				setHasLoadedImage(false);
				setIsLoading(true);
				return;
			}
			setIsLoading(false);
			setHasLoadedImage(false);
		}, ACCESS_PASS_IMAGE_TIMEOUT_MS);
		return () => window.clearTimeout(timeout);
	}, [candidateIndex, candidateKey, candidates.length, isLoading]);

	const tryNextCandidate = () => {
		if (candidateIndex < candidates.length - 1) {
			setCandidateIndex((index) => index + 1);
			setHasLoadedImage(false);
			setIsLoading(true);
			return;
		}
		setIsLoading(false);
		setHasLoadedImage(false);
	};

	return (
		<div className='relative h-full w-full overflow-hidden bg-black group'>
			<img
				src={DEFAULT_ACCESS_PASS_IMAGE}
				alt=''
				aria-hidden='true'
				className='absolute inset-0 h-full w-full object-cover'
			/>
			<img
				key={currentSrc}
				src={currentSrc}
				alt={nft.metadata.name || `Access Pass NFT ${nft.id}`}
				loading='eager'
				decoding='async'
				className={`absolute inset-0 h-full w-full ${
					objectFit === 'contain' ? 'object-contain' : 'object-cover'
				} transition-opacity duration-300 group-hover:scale-105 ${
					hasLoadedImage ? 'opacity-100' : 'opacity-0'
				} ${imageClassName}`}
				onLoad={() => {
					setHasLoadedImage(true);
					setIsLoading(false);
				}}
				onError={tryNextCandidate}
			/>
			{isLoading && (
				<div className='absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]'>
					<Loader className='scale-50' message='' />
				</div>
			)}
			{!isLoading && !hasLoadedImage && (
				<div className='absolute inset-0 flex items-center justify-center bg-black/30'>
					<User className={`${fallbackIconClassName} text-pink-400`} />
				</div>
			)}
		</div>
	);
};

const getProfileNftImageCandidates = (nft?: GameIdNftWithMetadata | null) =>
	nft ? getGameIdImageCandidates(nft) : [DEFAULT_GAME_ID_IMAGE];

const ProfileNftImage: React.FC<{
	nft: GameIdNftWithMetadata;
	title: string;
	icon: React.ComponentType<{ className?: string }>;
	accentClass: string;
}> = ({ nft, title, icon: Icon, accentClass }) => {
	const candidates = useMemo(() => getProfileNftImageCandidates(nft), [nft]);
	const candidateKey = candidates.join('\n');
	const [candidateIndex, setCandidateIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [hasLoadedImage, setHasLoadedImage] = useState(false);
	const currentSrc = candidates[candidateIndex] || DEFAULT_GAME_ID_IMAGE;

	useEffect(() => {
		setCandidateIndex(0);
		setIsLoading(true);
		setHasLoadedImage(false);
	}, [candidateKey]);

	useEffect(() => {
		if (!isLoading) return;
		const timeout = window.setTimeout(() => {
			if (candidateIndex < candidates.length - 1) {
				setCandidateIndex((index) => index + 1);
				setHasLoadedImage(false);
				setIsLoading(true);
				return;
			}
			setIsLoading(false);
		}, GAME_ID_IMAGE_TIMEOUT_MS);
		return () => window.clearTimeout(timeout);
	}, [candidateIndex, candidateKey, candidates.length, isLoading]);

	const tryNextCandidate = () => {
		if (candidateIndex < candidates.length - 1) {
			setCandidateIndex((index) => index + 1);
			setHasLoadedImage(false);
			setIsLoading(true);
			return;
		}
		setIsLoading(false);
		setHasLoadedImage(false);
	};

	return (
		<div className='relative h-full w-full overflow-hidden bg-black group'>
			<img
				src={DEFAULT_GAME_ID_IMAGE}
				alt=''
				aria-hidden='true'
				className='absolute inset-0 h-full w-full object-cover'
			/>
			<img
				key={currentSrc}
				src={currentSrc}
				alt={nft.metadata.name || `${title} ${nft.id}`}
				loading='eager'
				decoding='async'
				className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 group-hover:scale-105 ${
					hasLoadedImage ? 'opacity-100' : 'opacity-0'
				}`}
				onLoad={() => {
					setHasLoadedImage(true);
					setIsLoading(false);
				}}
				onError={tryNextCandidate}
			/>
			{isLoading && (
				<div className='absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]'>
					<Loader className='scale-50' message='' />
				</div>
			)}
			{!isLoading && !hasLoadedImage && (
				<div className='absolute inset-0 flex items-center justify-center bg-black/30'>
					<Icon className={`h-8 w-8 ${accentClass}`} />
				</div>
			)}
		</div>
	);
};

const getProfileNftLinks = (nft: GameIdNftWithMetadata) => {
	const isBase = nft.chainId === 8453;
	return {
		explorerLabel: isBase ? 'BaseScan' : 'PolygonScan',
		explorerBase: isBase ? 'https://basescan.org' : 'https://polygonscan.com',
		openSeaChain: isBase ? 'base' : 'matic',
	};
};

const ProfileNftCollectionCard: React.FC<{
	title: string;
	icon: React.ComponentType<{ className?: string }>;
	nfts: GameIdNftWithMetadata[];
	currentIndex: number;
	setCurrentIndex: (index: number) => void;
	isLoading: boolean;
	error?: string | null;
	accentClass: string;
	emptyMessage: string;
	copyToClipboard: (text: string, label: string) => void;
}> = ({
	title,
	icon: Icon,
	nfts,
	currentIndex,
	setCurrentIndex,
	isLoading,
	error,
	accentClass,
	emptyMessage,
	copyToClipboard,
}) => {
	const safeCurrentIndex =
		nfts.length > 0
			? Math.max(0, Math.min(currentIndex, nfts.length - 1))
			: 0;
	const activeNft = nfts[safeCurrentIndex];
	const canNavigate = nfts.length > 1;
	const links = activeNft ? getProfileNftLinks(activeNft) : null;

	useEffect(() => {
		if (nfts.length > 0 && (currentIndex < 0 || currentIndex >= nfts.length)) {
			setCurrentIndex(0);
		}
	}, [currentIndex, nfts.length, setCurrentIndex]);

	const move = (direction: -1 | 1) => {
		if (!canNavigate) return;
		setCurrentIndex(
			(((safeCurrentIndex + direction) % nfts.length) + nfts.length) %
				nfts.length
		);
	};

	return (
		<div className='p-4 rounded-xl bg-black/60 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(236,72,153,0.2)]'>
			<div className='flex items-center my-2 ml-3'>
				<Icon className={`h-5 w-5 mr-2 ${accentClass}`} />
				<p className={`text-xl font-semibold ${accentClass}`}>{title}</p>
				{canNavigate && (
					<span className='ml-4 text-sm text-white/60'>
						{safeCurrentIndex + 1} of {nfts.length}
					</span>
				)}
			</div>
			<Card className='bg-black/40 backdrop-blur-md border border-gray-950/10 rounded-xl'>
				<CardContent className='p-6'>
					{isLoading ? (
						<div className='relative aspect-square rounded-2xl overflow-hidden bg-black/60 border border-white/20'>
							<img
								src={DEFAULT_GAME_ID_IMAGE}
								alt=''
								aria-hidden='true'
								className='absolute inset-0 h-full w-full object-cover'
							/>
							<div className='absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]'>
								<Loader className='scale-50' message='' />
							</div>
						</div>
					) : activeNft ? (
						<div className='space-y-4'>
							<div className='relative aspect-square rounded-2xl overflow-hidden bg-black/60 border border-white/20'>
								{nfts.map((nft, index) => (
									<div
										key={getGameIdNftKey(nft)}
										aria-hidden={index !== safeCurrentIndex}
										className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
											index === safeCurrentIndex
												? 'opacity-100'
												: 'opacity-0 pointer-events-none'
										}`}
									>
										<ProfileNftImage
											nft={nft}
											title={title}
											icon={Icon}
											accentClass={accentClass}
										/>
									</div>
								))}
								{canNavigate && (
									<>
										<button
											type='button'
											aria-label={`Previous ${title}`}
											onClick={() => move(-1)}
											className='absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/80 p-2 text-white hover:bg-black/90'
										>
											<ArrowLeft className='h-5 w-5' />
										</button>
										<button
											type='button'
											aria-label={`Next ${title}`}
											onClick={() => move(1)}
											className='absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/80 p-2 text-white hover:bg-black/90'
										>
											<ArrowLeft className='h-5 w-5 rotate-180' />
										</button>
									</>
								)}
							</div>
							<div>
								<h4 className='font-semibold text-white'>
									{activeNft.metadata.name || `${title} #${activeNft.id}`}
								</h4>
								<p className='text-sm text-pink-200'>
									{activeNft.metadata.description || 'Watcher Society NFT'}
								</p>
							</div>
							<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-black/60 border border-white/20 rounded-lg'>
								<div>
									<span className='text-xs text-pink-300'>Contract</span>
									<div className='flex items-center gap-1'>
										<span className='font-mono text-sm text-white'>
											{activeNft.tokenAddress.slice(0, 6)}...
											{activeNft.tokenAddress.slice(-4)}
										</span>
										<Button
											variant='ghost'
											size='sm'
											className='p-1 text-pink-300 hover:text-pink-100 hover:bg-pink-800/30'
											onClick={() =>
												copyToClipboard(
													activeNft.tokenAddress,
													'Contract Address'
												)
											}
										>
											<Copy className='h-3 w-3' />
										</Button>
									</div>
								</div>
								<div>
									<span className='text-xs text-pink-300'>Token ID</span>
									<div className='font-mono text-2xl text-white'>
										{activeNft.id}
									</div>
								</div>
							</div>
							{links && (
								<div className='flex flex-wrap gap-2'>
									<a
										href={`${links.explorerBase}/nft/${activeNft.tokenAddress}/${activeNft.id}`}
										target='_blank'
										rel='noopener noreferrer'
										className='inline-flex items-center gap-1 px-3 py-1 bg-purple-900/30 text-purple-200 border border-purple-500/30 rounded-md text-sm hover:bg-purple-900/50 transition-colors'
									>
										<ExternalLink className='h-3 w-3' />
										{links.explorerLabel}
									</a>
									<a
										href={`https://opensea.io/assets/${links.openSeaChain}/${activeNft.tokenAddress}/${activeNft.id}`}
										target='_blank'
										rel='noopener noreferrer'
										className='inline-flex items-center gap-1 px-3 py-1 bg-emerald-900/30 text-emerald-200 border border-emerald-500/30 rounded-md text-sm hover:bg-emerald-900/50 transition-colors'
									>
										<ExternalLink className='h-3 w-3' />
										OpenSea
									</a>
								</div>
							)}
						</div>
					) : (
						<div className='space-y-4 text-center'>
							<div className='relative aspect-square rounded-2xl overflow-hidden bg-black/60 border border-white/20'>
								<img
									src={DEFAULT_GAME_ID_IMAGE}
									alt=''
									aria-hidden='true'
									className='absolute inset-0 h-full w-full object-cover opacity-70'
								/>
								<div className='absolute inset-0 bg-black/45' />
								<div className='absolute inset-0 flex items-center justify-center'>
									<Icon className={`h-12 w-12 mx-auto mb-4 ${accentClass}`} />
									<p className={`text-2xl font-bold ${accentClass}`}>
										{emptyMessage}
									</p>
								</div>
							</div>
							{error && (
								<p className='text-sm text-red-300 break-words'>{error}</p>
							)}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

// Hover tooltip component for access rewards
interface AccessRewardTooltipProps {
	reward: {
		amount: number;
		aiName: string;
		variation?: {
			warning: string;
			risk: string;
			quirk: string;
			closer: string;
		};
	};
	mousePosition: { x: number; y: number };
}

const AccessRewardTooltip: React.FC<AccessRewardTooltipProps> = ({
	reward,
	mousePosition,
}) => {
	if (!reward.variation) return null;

	return (
		<div
			className='fixed z-50 pointer-events-none'
			style={{
				left: `${mousePosition.x + 15}px`,
				top: `${mousePosition.y - 10}px`,
			}}
		>
			<div className='bg-black/90 backdrop-blur-md border border-pink-500/50 rounded-xl p-4 shadow-[0_0_20px_rgba(236,72,153,0.5)] max-w-sm'>
				{/* Mini version of the 4 areas */}
				<div className='space-y-3'>
					{/* AI Name Header */}
					<div className='text-center mb-3'>
						<div className='text-sm text-pink-400/80 uppercase tracking-wide'>
							A.I. AGENT
						</div>
						<div className='text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400'>
							{reward.aiName}
						</div>
					</div>

					{/* Warning */}
					<div className='p-3 bg-white/5 backdrop-blur-sm border border-pink-500/20 rounded-lg'>
						<div className='text-xs text-pink-400/80 uppercase tracking-wide mb-1 font-semibold'>
							⚠️ WARNING
						</div>
						<div className='text-xs text-white/90 leading-tight line-clamp-2'>
							{reward.variation.warning}
						</div>
					</div>

					{/* Risk */}
					<div className='p-3 bg-white/5 backdrop-blur-sm border border-orange-500/20 rounded-lg'>
						<div className='text-xs text-orange-400/80 uppercase tracking-wide mb-1 font-semibold'>
							🔥 RISK
						</div>
						<div className='text-xs text-white/90 leading-tight line-clamp-2'>
							{reward.variation.risk}
						</div>
					</div>

					{/* Quirk */}
					<div className='p-3 bg-white/5 backdrop-blur-sm border border-purple-500/20 rounded-lg'>
						<div className='text-xs text-purple-400/80 uppercase tracking-wide mb-1 font-semibold'>
							😈 QUIRK
						</div>
						<div className='text-xs text-white/90 leading-tight line-clamp-2'>
							{reward.variation.quirk}
						</div>
					</div>

					{/* Closer */}
					<div className='p-3 bg-white/5 backdrop-blur-sm border border-pink-500/20 rounded-lg'>
						<div className='text-xs text-pink-400/80 uppercase tracking-wide mb-1 font-semibold'>
							💭 MESSAGE
						</div>
						<div className='text-xs text-white/90 leading-tight line-clamp-2'>
							{reward.variation.closer}
						</div>
					</div>

					{/* Reward Amount */}
					<div className='text-center pt-2 border-t border-pink-500/20'>
						<div className='text-lg font-bold text-orange-600 dark:text-orange-400'>
							{reward.amount} $KARMA
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export const UserProfile: React.FC = () => {
	const { userId } = useParams();
	const { user } = useAuth();
	const { getMemberAuthToken } = useWeb3Auth();
	const updateUserName = useMutation(api.users.updateUserNameAuthorized);
	const fetchNftsFromBlockchain = useAction(
		api.gameIdNfts.fetchGameIdNftsFromBlockchain
	);
	const fetchNftsWithCache = useCallback(
		async ({
			walletAddress,
			contractAddress,
		}: {
			walletAddress: string;
			contractAddress: string;
		}): Promise<BlockchainNftReadResult> => {
			const cacheKey = getNftReadCacheKey(walletAddress, contractAddress);
			const cached = readCachedNftResult(cacheKey);
			if (cached) return cached;

			const existing = inFlightNftReads.get(cacheKey);
			if (existing) return existing;

			const request = fetchNftsFromBlockchain({
				walletAddress,
				contractAddress,
			}) as Promise<BlockchainNftReadResult>;
			inFlightNftReads.set(cacheKey, request);
			try {
				const result = await request;
				if (result?.success) writeCachedNftResult(cacheKey, result);
				return result;
			} finally {
				inFlightNftReads.delete(cacheKey);
			}
		},
		[fetchNftsFromBlockchain]
	);

	// Separate state for profile viewing and editing
	// const [isNameHovered, setIsNameHovered] = useState(false);
	const [isNameEditable, setIsNameEditable] = useState(false);
	const [editedName, setEditedName] = useState('');

	// Affiliate system state
	const [showAffiliateStats, setShowAffiliateStats] = useState(false);

	// Access Pass NFT modal state
	const [showAccessPassModal, setShowAccessPassModal] = useState(false);

	// Access Pass NFT Carousel state (similar to Game ID carousel)
	const [currentAccessPassIndex, setCurrentAccessPassIndex] = useState(0);
	const [isAccessPassAnimating, setIsAccessPassAnimating] = useState(false);
	const [accessPassFadeState, setAccessPassFadeState] = useState<
		'in' | 'out' | 'none'
	>('none');
	const [accessPassSlideDirection, setAccessPassSlideDirection] = useState<
		'left' | 'right'
	>('left');

	// Access Rewards Carousel state
	const [currentAccessRewardIndex, setCurrentAccessRewardIndex] = useState(0);
	const [isAccessRewardAnimating, setIsAccessRewardAnimating] =
		useState(false);
	const [accessRewardFadeState, setAccessRewardFadeState] = useState<
		'in' | 'out' | 'none'
	>('none');
	const [accessRewardSlideDirection, setAccessRewardSlideDirection] =
		useState<'left' | 'right'>('left');

	// Hover state for access rewards tooltip
	const [hoveredReward, setHoveredReward] = useState<{
		amount: number;
		aiName: string;
		variation?: {
			warning: string;
			risk: string;
			quirk: string;
			closer: string;
		};
	} | null>(null);
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

	// Blockchain NFT state
	const [blockchainNfts, setBlockchainNfts] = useState<
		GameIdNftWithMetadata[]
	>([]);
	const [isLoadingNfts, setIsLoadingNfts] = useState(false);
	const [nftError, setNftError] = useState<string | null>(null);

	// Access Pass NFT state
	const [accessPassNfts, setAccessPassNfts] = useState<
		AccessPassNftWithMetadata[]
	>([]);
	const [isLoadingAccessPass, setIsLoadingAccessPass] = useState(false);
	const [accessPassError, setAccessPassError] = useState<string | null>(null);
	const [watcherBuilderNfts, setWatcherBuilderNfts] = useState<
		GameIdNftWithMetadata[]
	>([]);
	const [isLoadingWatcherBuilder, setIsLoadingWatcherBuilder] =
		useState(false);
	const [watcherBuilderError, setWatcherBuilderError] = useState<string | null>(
		null
	);
	const [currentWatcherBuilderIndex, setCurrentWatcherBuilderIndex] =
		useState(0);
	const [watcherPlayerNfts, setWatcherPlayerNfts] = useState<
		GameIdNftWithMetadata[]
	>([]);
	const [isLoadingWatcherPlayer, setIsLoadingWatcherPlayer] = useState(false);
	const [watcherPlayerError, setWatcherPlayerError] = useState<string | null>(
		null
	);
	const [currentWatcherPlayerIndex, setCurrentWatcherPlayerIndex] = useState(0);

	// NFT Carousel state (similar to VideoTimelinePage)
	const [currentNftIndex, setCurrentNftIndex] = useState(0);
	const [isNftAnimating, setIsNftAnimating] = useState(false);
	const [nftFadeState, setNftFadeState] = useState<'in' | 'out' | 'none'>(
		'none'
	);
	const [nftSlideDirection, setNftSlideDirection] = useState<
		'left' | 'right'
	>('left');

	const inputRef = useRef<HTMLInputElement>(null);

	// If we have a userId from the URL, use that
	// Otherwise use the current authenticated user
	const queryResult = useQuery(
		api.users.getById,
		userId ? { userId: userId as Id<'users'> } : 'skip'
	);

	// Use queryResult if it exists, otherwise use current user
	const userData = useMemo(
		() =>
			(queryResult ||
				(user ? { ...user } : null)) as UserWithGameId | null,
		[queryResult, user]
	);

	// Query Game ID NFT data for the user (from database)
	const gameIdNftData = useQuery(
		api.gameIdNfts.getGameIdNftWithMetadata,
		userData ? { userId: userData._id } : 'skip'
	);

	// Query access rewards history for the user
	const accessRewards = useQuery(
		api.gamifiedWarning.getWarningRewardHistory,
		userData ? { userId: userData._id, limit: 10 } : 'skip'
	);

	// Fetch NFT metadata from blockchain using secure Convex action
	const fetchBlockchainNfts = useCallback(
		async (walletAddress: string) => {
			if (!walletAddress) return;

			setIsLoadingNfts(true);
			setNftError(null);

			try {
				console.log(
					'🔍 Fetching blockchain NFTs for wallet:',
					walletAddress
				);

				const result = await fetchNftsWithCache({
					walletAddress,
					contractAddress: WATCHER_GAME_ID_CONTRACT_ADDRESS,
				});

				if (result.success) {
					setBlockchainNfts(result.nfts);
					console.log(
						'✅ Successfully fetched',
						result.nfts.length,
						'NFTs from blockchain'
					);
				} else {
					setNftError(
						result.error ||
							'Failed to fetch NFT data from blockchain'
					);
				}
			} catch (error) {
				console.error('❌ Error fetching blockchain NFTs:', error);
				setNftError('Error loading NFT data from blockchain');
			} finally {
				setIsLoadingNfts(false);
			}
		},
		[fetchNftsWithCache]
	);

	// Fetch Access Pass NFT metadata from blockchain using secure Convex action
	const fetchAccessPassNfts = useCallback(
		async (walletAddress: string) => {
			if (!walletAddress) return;

			setIsLoadingAccessPass(true);
			setAccessPassError(null);

			try {
				console.log(
					'🔍 Fetching access pass NFTs from multiple contracts for wallet:',
					walletAddress
				);

				const accessPassContractAddresses =
					WATCHER_ACCESS_PASS_CONTRACT_ADDRESSES;

				if (accessPassContractAddresses.length === 0) {
					throw new Error(
						'No access pass contract addresses configured'
					);
				}

				const allNfts: AccessPassNftWithMetadata[] = [];
				const errors: string[] = [];

				for (const contractAddress of accessPassContractAddresses) {
					try {
						console.log(
							'🔍 Fetching from access pass contract:',
							contractAddress
						);
						const result = await fetchNftsWithCache({
							walletAddress,
							contractAddress,
						});

						if (result.success) {
							allNfts.push(...result.nfts);
							console.log(
								'✅ Successfully fetched',
								result.nfts.length,
								'access pass NFTs from blockchain'
							);
						} else {
							errors.push(
								`Access Pass ${contractAddress.slice(0, 10)}...: ${result.error || 'Failed to fetch'}`
							);
						}
					} catch (error) {
						console.error(
							'❌ Error fetching access pass NFTs:',
							error
						);
						errors.push(
							`Access Pass ${contractAddress.slice(0, 10)}...: ${error instanceof Error ? error.message : 'Unknown error'}`
						);
					}
				}

				const uniqueNfts = Array.from(
					new Map(
						allNfts.map((nft) => [
							`${nft.tokenAddress?.toLowerCase() || 'unknown'}:${nft.id}`,
							nft,
						])
					).values()
				);

				// Set the combined results
				setAccessPassNfts(uniqueNfts);
				console.log(
					'✅ Total access pass NFTs fetched:',
					uniqueNfts.length,
					'from',
					accessPassContractAddresses.length,
					'contracts'
				);

				// Set error only if we have errors and no NFTs
				if (errors.length > 0 && uniqueNfts.length === 0) {
					setAccessPassError(errors.join('; '));
				}
			} catch (error) {
				console.error('❌ Error fetching access pass NFTs:', error);
				setAccessPassError(
					'Error loading access pass data from blockchain'
				);
			} finally {
				setIsLoadingAccessPass(false);
			}
		},
		[fetchNftsWithCache]
	);

	const fetchWatcherCollectionNfts = useCallback(
		async ({
			walletAddress,
			contractAddress,
			setNfts,
			setLoading,
			setError,
			label,
		}: {
			walletAddress: string;
			contractAddress: string;
			setNfts: (nfts: GameIdNftWithMetadata[]) => void;
			setLoading: (isLoading: boolean) => void;
			setError: (error: string | null) => void;
			label: string;
		}) => {
			if (!walletAddress || !contractAddress) return;
			setLoading(true);
			setError(null);
			try {
				const result = await fetchNftsWithCache({
					walletAddress,
					contractAddress,
				});
				if (result.success) {
					setNfts(result.nfts);
				} else {
					setNfts([]);
					setError(result.error || `Failed to fetch ${label} NFTs`);
				}
			} catch (error) {
				console.error(`❌ Error fetching ${label} NFTs:`, error);
				setNfts([]);
				setError(`Error loading ${label} NFT data`);
			} finally {
				setLoading(false);
			}
		},
		[fetchNftsWithCache]
	);

	const fetchWatcherBuilderNfts = useCallback(
		(walletAddress: string) =>
			fetchWatcherCollectionNfts({
				walletAddress,
				contractAddress: WATCHER_BUILDER_CONTRACT_ADDRESS,
				setNfts: setWatcherBuilderNfts,
				setLoading: setIsLoadingWatcherBuilder,
				setError: setWatcherBuilderError,
				label: 'Watcher Builder',
			}),
		[fetchWatcherCollectionNfts]
	);

	const fetchWatcherPlayerNfts = useCallback(
		(walletAddress: string) =>
			fetchWatcherCollectionNfts({
				walletAddress,
				contractAddress: WATCHER_PLAYER_CONTRACT_ADDRESS,
				setNfts: setWatcherPlayerNfts,
				setLoading: setIsLoadingWatcherPlayer,
				setError: setWatcherPlayerError,
				label: 'Watcher Player',
			}),
		[fetchWatcherCollectionNfts]
	);

	// Effect to fetch blockchain NFTs when wallet address changes
	useEffect(() => {
		const walletAddress = userData?.walletAddress;
		if (!walletAddress) {
			setBlockchainNfts([]);
			setNftError(null);
			setAccessPassNfts([]);
			setAccessPassError(null);
			setWatcherBuilderNfts([]);
			setWatcherBuilderError(null);
			setWatcherPlayerNfts([]);
			setWatcherPlayerError(null);
			setIsLoadingNfts(false);
			setIsLoadingAccessPass(false);
			setIsLoadingWatcherBuilder(false);
			setIsLoadingWatcherPlayer(false);
			return;
		}

		fetchBlockchainNfts(walletAddress);
		fetchAccessPassNfts(walletAddress);
		fetchWatcherBuilderNfts(walletAddress);
		fetchWatcherPlayerNfts(walletAddress);
	}, [
		userData?.walletAddress,
		fetchBlockchainNfts,
		fetchAccessPassNfts,
		fetchWatcherBuilderNfts,
		fetchWatcherPlayerNfts,
	]);

	const blockchainGameId = useMemo(() => {
		for (const nft of blockchainNfts) {
			const gameIdAttribute = nft.metadata.attributes?.find(
				(attr) =>
					attr.trait_type?.toLowerCase() === 'game id' ||
					attr.trait_type?.toLowerCase() === 'gameid' ||
					attr.trait_type?.toLowerCase() === 'game_id'
			);
			const value = gameIdAttribute?.value?.toString().trim();
			if (value) return value;
		}
		return '';
	}, [blockchainNfts]);

	const resolvedGameId = userData?.gameId || blockchainGameId;

	// Sort NFTs to show the one matching gameId first
	const sortedNfts = useMemo(() => {
		if (!blockchainNfts.length || !resolvedGameId) return blockchainNfts;

		const gameIdMatch = blockchainNfts.find((nft) =>
			nft.metadata.attributes?.some(
				(attr) =>
					attr.trait_type?.toLowerCase() === 'game id' &&
					attr.value?.toString().toLowerCase() ===
						resolvedGameId.toLowerCase()
			)
		);

		if (!gameIdMatch) return blockchainNfts;

		const others = blockchainNfts.filter((nft) => nft !== gameIdMatch);
		return [gameIdMatch, ...others];
	}, [blockchainNfts, resolvedGameId]);
	const sortedNftKey = useMemo(
		() => sortedNfts.map(getGameIdNftKey).join('|'),
		[sortedNfts]
	);

	// NFT Carousel navigation handlers (similar to VideoTimelinePage)
	const handlePrevNft = useCallback(() => {
		if (isNftAnimating || sortedNfts.length <= 1) return;

		const nextIndex =
			(((currentNftIndex - 1) % sortedNfts.length) + sortedNfts.length) %
			sortedNfts.length;

		setNftSlideDirection('right');
		setIsNftAnimating(true);
		setNftFadeState('out');

		setTimeout(() => {
			setCurrentNftIndex(nextIndex);
			setNftFadeState('in');

			setTimeout(() => {
				setIsNftAnimating(false);
				setNftFadeState('none');
			}, 300);
		}, 300);
	}, [isNftAnimating, sortedNfts.length, currentNftIndex]);

	const handleNextNft = useCallback(() => {
		if (isNftAnimating || sortedNfts.length <= 1) return;

		const nextIndex = (currentNftIndex + 1) % sortedNfts.length;

		setNftSlideDirection('left');
		setIsNftAnimating(true);
		setNftFadeState('out');

		setTimeout(() => {
			setCurrentNftIndex(nextIndex);
			setNftFadeState('in');

			setTimeout(() => {
				setIsNftAnimating(false);
				setNftFadeState('none');
			}, 300);
		}, 300);
	}, [isNftAnimating, sortedNfts.length, currentNftIndex]);

	const handleSelectNft = useCallback(
		(nextIndex: number) => {
			if (
				isNftAnimating ||
				nextIndex === currentNftIndex ||
				nextIndex < 0 ||
				nextIndex >= sortedNfts.length
			) {
				return;
			}

			setNftSlideDirection(
				nextIndex > currentNftIndex ? 'left' : 'right'
			);
			setIsNftAnimating(true);
			setNftFadeState('out');

			setTimeout(() => {
				setCurrentNftIndex(nextIndex);
				setNftFadeState('in');

				setTimeout(() => {
					setIsNftAnimating(false);
					setNftFadeState('none');
				}, 300);
			}, 300);
		},
		[isNftAnimating, currentNftIndex, sortedNfts.length]
	);

	// Reset current index when NFTs change
	useEffect(() => {
		setCurrentNftIndex(0);
		setNftFadeState('none');
		setIsNftAnimating(false);
	}, [sortedNftKey]);

	// Access Pass NFT Carousel navigation handlers (similar to Game ID carousel)
	const handlePrevAccessPass = useCallback(() => {
		if (isAccessPassAnimating || accessPassNfts.length <= 1) return;

		const nextIndex =
			(((currentAccessPassIndex - 1) % accessPassNfts.length) +
				accessPassNfts.length) %
			accessPassNfts.length;

		setAccessPassSlideDirection('right');
		setIsAccessPassAnimating(true);
		setAccessPassFadeState('out');

		setTimeout(() => {
			setCurrentAccessPassIndex(nextIndex);
			setAccessPassFadeState('in');

			setTimeout(() => {
				setIsAccessPassAnimating(false);
				setAccessPassFadeState('none');
			}, 150);
		}, 150);
	}, [isAccessPassAnimating, accessPassNfts.length, currentAccessPassIndex]);

	const handleNextAccessPass = useCallback(() => {
		if (isAccessPassAnimating || accessPassNfts.length <= 1) return;

		const nextIndex = (currentAccessPassIndex + 1) % accessPassNfts.length;

		setAccessPassSlideDirection('left');
		setIsAccessPassAnimating(true);
		setAccessPassFadeState('out');

		setTimeout(() => {
			setCurrentAccessPassIndex(nextIndex);
			setAccessPassFadeState('in');

			setTimeout(() => {
				setIsAccessPassAnimating(false);
				setAccessPassFadeState('none');
			}, 150);
		}, 150);
	}, [isAccessPassAnimating, accessPassNfts.length, currentAccessPassIndex]);

	// Reset access pass index when NFTs change
	useEffect(() => {
		setCurrentAccessPassIndex(0);
		setAccessPassFadeState('none');
		setIsAccessPassAnimating(false);
	}, [accessPassNfts.length]);

	useEffect(() => {
		setCurrentWatcherBuilderIndex(0);
	}, [watcherBuilderNfts.length]);

	useEffect(() => {
		setCurrentWatcherPlayerIndex(0);
	}, [watcherPlayerNfts.length]);

	// Access Rewards Carousel navigation handlers
	const handlePrevAccessReward = useCallback(() => {
		if (
			isAccessRewardAnimating ||
			!accessRewards ||
			accessRewards.length <= 1
		)
			return;

		const nextIndex =
			(((currentAccessRewardIndex - 1) % accessRewards.length) +
				accessRewards.length) %
			accessRewards.length;

		setAccessRewardSlideDirection('right');
		setIsAccessRewardAnimating(true);
		setAccessRewardFadeState('out');

		setTimeout(() => {
			setCurrentAccessRewardIndex(nextIndex);
			setAccessRewardFadeState('in');

			setTimeout(() => {
				setIsAccessRewardAnimating(false);
				setAccessRewardFadeState('none');
			}, 150);
		}, 150);
	}, [isAccessRewardAnimating, accessRewards, currentAccessRewardIndex]);

	const handleNextAccessReward = useCallback(() => {
		if (
			isAccessRewardAnimating ||
			!accessRewards ||
			accessRewards.length <= 1
		)
			return;

		const nextIndex = (currentAccessRewardIndex + 1) % accessRewards.length;

		setAccessRewardSlideDirection('left');
		setIsAccessRewardAnimating(true);
		setAccessRewardFadeState('out');

		setTimeout(() => {
			setCurrentAccessRewardIndex(nextIndex);
			setAccessRewardFadeState('in');

			setTimeout(() => {
				setIsAccessRewardAnimating(false);
				setAccessRewardFadeState('none');
			}, 150);
		}, 150);
	}, [isAccessRewardAnimating, accessRewards, currentAccessRewardIndex]);

	// Reset access reward index when rewards change
	useEffect(() => {
		setCurrentAccessRewardIndex(0);
		setAccessRewardFadeState('none');
		setIsAccessRewardAnimating(false);
	}, [accessRewards?.length]);

	// Preload access pass images for smooth navigation
	useEffect(() => {
		if (accessPassNfts.length > 1) {
			const preloadImage = (src: string) => {
				const img = new Image();
				img.src = src;
			};

			// Preload next and previous images
			const nextIndex =
				(currentAccessPassIndex + 1) % accessPassNfts.length;
			const prevIndex =
				(((currentAccessPassIndex - 1) % accessPassNfts.length) +
					accessPassNfts.length) %
				accessPassNfts.length;

			const nextNft = accessPassNfts[nextIndex];
			const prevNft = accessPassNfts[prevIndex];

			if (nextNft) {
				getAccessPassImageCandidates(nextNft).forEach(preloadImage);
			}
			if (prevNft) {
				getAccessPassImageCandidates(prevNft).forEach(preloadImage);
			}
		}
	}, [accessPassNfts, currentAccessPassIndex]);

	useEffect(() => {
		const seen = new Set<string>();
		[...watcherBuilderNfts, ...watcherPlayerNfts].forEach((nft) => {
			getProfileNftImageCandidates(nft).forEach((src) => {
				if (!src || seen.has(src)) return;
				seen.add(src);
				const img = new Image();
				img.src = src;
			});
		});
	}, [watcherBuilderNfts, watcherPlayerNfts]);

	// Debug logging for affiliate system
	React.useEffect(() => {
		console.log('🔍 Admin UserProfile Debug:', {
			userData: userData
				? {
						id: userData._id,
						email: userData.email,
						gameId: userData.gameId,
						hasGameId: !!userData.gameId,
						walletAddress: userData.walletAddress,
					}
				: null,
			gameIdNftData,
			blockchainNfts: blockchainNfts.length,
		});
	}, [userData, gameIdNftData, blockchainNfts]);

	if (!userData) {
		return (
			<div className='container mx-auto p-6'>
				<div className='flex items-center justify-center h-64'>
					<div className='text-lg text-muted-foreground'>
						{userId
							? 'Loading user data...'
							: 'Please log in to view your profile'}
					</div>
				</div>
			</div>
		);
	}

	// Determine if this is the user's own profile
	const isOwnProfile =
		!userId || (user && userData._id.toString() === user._id.toString());
	const safeCurrentAccessPassIndex =
		accessPassNfts.length > 0
			? Math.min(currentAccessPassIndex, accessPassNfts.length - 1)
			: 0;
	const currentAccessPassNft = accessPassNfts[safeCurrentAccessPassIndex];

	// Copy to clipboard helper function
	const copyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text);
		toast({
			title: 'Copied!',
			description: `${label} copied to clipboard`,
			duration: 2000,
		});
	};

	// Handle name editing
	const startEditing = () => {
		setEditedName(userData.name);
		setIsNameEditable(true);
		setTimeout(() => {
			inputRef.current?.focus();
			inputRef.current?.select();
		}, 0);
	};

	const cancelEditing = () => {
		setIsNameEditable(false);
		setEditedName('');
	};

	const saveNameChange = async () => {
		if (editedName.trim() && editedName !== userData!.name) {
			try {
				if (!getMemberAuthToken) throw new Error('MEMBER_SIGNED_AUTH_REQUIRED');
				await updateUserName({
					userId: userData!._id,
					name: editedName.trim(),
					memberToken: await getMemberAuthToken(),
				});
				toast({
					title: 'Name Updated',
					description: 'Your name has been successfully updated.',
				});
			} catch {
				toast({
					title: 'Error',
					description: 'Failed to update name. Please try again.',
					variant: 'destructive',
				});
			}
		}
		setIsNameEditable(false);
		setEditedName('');
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			saveNameChange();
		} else if (e.key === 'Escape') {
			cancelEditing();
		}
	};

	// Function to get NFT display status
	const getNftDisplayStatus = () => {
		if (!gameIdNftData) {
			return 'loading';
		}

		if (gameIdNftData.pendingMint) {
			return 'pending';
		}

		if (gameIdNftData.transactionHash) {
			return 'minted';
		}

		return 'none';
	};

	const renderGameIdSection = () => {
		const nftStatus = getNftDisplayStatus();

		// Show loader when loading NFTs or when user data is still loading
		if (isLoadingNfts && (resolvedGameId || userData!.walletAddress)) {
			return (
				<GameIdLoadingCard gameId={resolvedGameId} />
			);
		}

		if (
			(resolvedGameId || blockchainNfts.length > 0) &&
			(nftStatus !== 'none' || blockchainNfts.length > 0)
		) {
			// User has a Game ID and either NFT data from database OR blockchain NFTs
			return (
				<div className='space-y-3'>
					{/* <div className='flex items-center justify-between'>
						<div className='flex items-center gap-2'>
							<span className='font-medium text-lg text-foreground/80 dark:text-white/80'>
								{userData!.gameId}
							</span>
							{nftStatus === 'minted' && (
								<CheckCircle className='h-5 w-5 text-green-500' />
							)}
							{nftStatus === 'pending' && (
								<div className='h-5 w-5 rounded-full border-2 border-orange-500 border-t-transparent animate-spin' />
							)}
						</div>
						<div className='flex items-center gap-2'>
							<Button
								variant='ghost'
								size='sm'
								className='p-1 text-primary hover:text-primary/80 dark:text-pink-300 dark:hover:text-pink-200'
								onClick={() =>
									copyToClipboard(
										`https://${userData!.gameId}.watchersociety.com`,
										'Affiliate Link'
									)
								}
							>
								<Copy className='h-4 w-4' />
							</Button>
							{userData!.walletAddress && (
								<Button
									variant='ghost'
									size='sm'
									className='p-1 text-blue-500 hover:text-blue-400'
									onClick={() =>
										fetchBlockchainNfts(
											userData!.walletAddress!
										)
									}
									disabled={isLoadingNfts}
								>
									<RefreshCw
										className={`h-4 w-4 ${isLoadingNfts ? 'animate-spin' : ''}`}
									/>
								</Button>
							)}
						</div>
					</div>

					<p className='text-sm text-muted-foreground'>
						{userData!.gameId}.watchersociety.com
					</p> */}

					{/* Loading state */}
					{isLoadingNfts && (
						<div className='mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl'>
							<div className='flex items-center gap-2'>
								<RefreshCw className='h-4 w-4 animate-spin text-blue-900' />
								<span className='text-blue-900 dark:text-blue-100'>
									Loading NFT data from blockchain...
								</span>
							</div>
						</div>
					)}

					{/* Error state */}
					{nftError && !isLoadingNfts && (
						<div className='mt-4 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl'>
							<p className='text-red-900 dark:text-red-100'>
								{nftError}
							</p>
						</div>
					)}

					{/* Enhanced OpenSea-Style NFT Display - Carousel */}
					{!isLoadingNfts && sortedNfts.length > 0 ? (
						<div className='space-y-4'>
							{/* NFT Carousel Container */}
							<div className='relative'>
								{/* Current NFT Display */}
								<div
									style={{
										opacity: nftFadeState === 'out' ? 0 : 1,
										transition: 'opacity 0.3s ease-in-out',
										willChange: 'opacity',
									}}
								>
									{(() => {
										const nft = sortedNfts[currentNftIndex];
										if (!nft) return null;

										const isGameIdMatch =
											resolvedGameId &&
											nft.metadata.attributes?.some(
												(attr) =>
													attr.trait_type?.toLowerCase() ===
														'game id' &&
													attr.value
														?.toString()
														.toLowerCase() ===
														resolvedGameId.toLowerCase()
											);
										return (
											<div className='mt-4 p-4 rounded-2xl bg-black/60 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(236,72,153,0.2)]'>
												<div className='flex items-center justify-between mb-3'>
													<h4 className='text-lg font-semibold text-white'>
														GAME ID #{nft.id}
														{isGameIdMatch && (
															<span className='ml-2 text-white text-sm rounded-xl bg-green-600 px-2 py-1'>
																ACTIVE
															</span>
														)}
													</h4>
													<div className='flex items-center gap-3'>
														<Badge className='bg-pink-500/20 text-pink-100 border border-pink-400/30 px-2 py-1 rounded-xl text-xs font-mono'>
															WSGID
														</Badge>
														<div className='bg-orange-600/90 text-white px-2 py-1 rounded-xl text-xs font-mono'>
															{nft.type}
														</div>
													</div>
												</div>

												{/* NFT Image */}
												<GameIdNftCarouselImages
													nfts={sortedNfts}
													currentIndex={
														currentNftIndex
													}
													onPrev={handlePrevNft}
													onNext={handleNextNft}
													onSelect={
														handleSelectNft
													}
												/>

												{/* NFT Metadata */}
												<div className='space-y-8'>
													<div>
														<h5 className='text-lg font-bold text-white'>
															{nft.metadata
																.name ||
																`Game ID NFT ${nft.id}`}
														</h5>
														<p className='text-sm text-pink-200'>
															{nft.metadata
																.description ||
																'Game ID NFT for the Watcher Society ecosystem'}
														</p>
													</div>

													{/* Contract & Token Details */}
													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-black/60 border border-white/20 rounded-lg'>
														<div>
															<span className='text-xs text-pink-300'>
																Contract
															</span>
															<div className='flex items-center gap-1'>
																<span className='font-mono text-sm text-white'>
																	{nft.tokenAddress.slice(
																		0,
																		6
																	)}
																	...
																	{nft.tokenAddress.slice(
																		-4
																	)}
																</span>
																<Button
																	variant='ghost'
																	size='sm'
																	className='p-1 text-pink-300 hover:text-pink-100 hover:bg-pink-800/30'
																	onClick={() =>
																		copyToClipboard(
																			nft.tokenAddress,
																			'Contract Address'
																		)
																	}
																>
																	<Copy className='h-3 w-3' />
																</Button>
															</div>
														</div>
														<div>
															<span className='text-xs text-pink-300'>
																Token ID
															</span>
															<div className='font-mono text-2xl text-white'>
																{nft.id}
															</div>
														</div>
													</div>

													{/* Attributes */}
													{nft.metadata.attributes &&
														nft.metadata.attributes
															.length > 0 && (
															<div className=''>
																<h6 className='text-sm font-semibold text-white mb-2'>
																	ATTRIBUTES
																</h6>
																<div className='grid grid-cols-1 gap-2'>
																	{nft.metadata.attributes.map(
																		(
																			attr,
																			attrIndex
																		) => (
																			<div
																				key={
																					attrIndex
																				}
																				className='p-4 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-center shadow-[0_0_15px_rgba(236,72,153,0.2)]'
																			>
																				<div className='text-sm text-pink-300 uppercase tracking-wide mb-1 break-words'>
																					{attr.trait_type ===
																					'DAO Weight Multiplier'
																						? 'DAO Weight'
																						: attr.trait_type}
																				</div>
																				<div
																					className={`${attr.trait_type === 'GAME ID' ? 'text-2xl' : 'text-sm'} font-medium text-white break-all`}
																				>
																					{
																						attr.value
																					}
																				</div>
																			</div>
																		)
																	)}
																</div>
															</div>
														)}

													{/* External Links */}
													<div>
														<h6 className='text-sm font-semibold text-foreground dark:text-white mb-3'>
															LINKS
														</h6>
														<div className='space-y-4'>
															{nft.metadata
																.external_url && (
																<div className='flex flex-wrap gap-2'>
																	<a
																		href={
																			nft
																				.metadata
																				.external_url
																		}
																		target='_blank'
																		rel='noopener noreferrer'
																		className='inline-flex items-center gap-1 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors'
																	>
																		<ExternalLink className='h-3 w-3' />
																		Visit
																		Site
																	</a>
																	<button
																		onClick={() =>
																			copyToClipboard(
																				nft
																					.metadata
																					.external_url!,
																				'External Site URL'
																			)
																		}
																		className='inline-flex items-center gap-1 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors cursor-pointer'
																	>
																		<Copy className='h-3 w-3' />
																		Copy URL
																	</button>
																</div>
															)}

																<div>
																	<a
																		href={`https://polygonscan.com/nft/${nft.tokenAddress}/${nft.id}`}
																	target='_blank'
																	rel='noopener noreferrer'
																	className='inline-flex items-center gap-1 px-3 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-md text-sm hover:bg-purple-100 dark:hover:bg-purple-700/60 transition-colors'
																>
																	<ExternalLink className='h-3 w-3' />
																	View on
																	PolygonScan
																</a>
															</div>

															<div>
																<a
																	href={`https://opensea.io/assets/matic/${nft.tokenAddress}/${nft.id}`}
																	target='_blank'
																	rel='noopener noreferrer'
																	className='inline-flex items-center gap-1 px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md text-sm hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors'
																>
																	<ExternalLink className='h-3 w-3' />
																	View on
																	OpenSea
																</a>
															</div>

															{/* {nft.tokenURI && (
																<div>
																	<a
																		href={
																			nft.tokenURI
																		}
																		target='_blank'
																		rel='noopener noreferrer'
																		className='inline-flex items-center gap-1 px-3 py-1 bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-900/30 transition-colors'
																	>
																		<ExternalLink className='h-3 w-3' />
																		View
																		Metadata
																	</a>
																</div>
															)} */}
														</div>
													</div>
												</div>
											</div>
										);
									})()}
								</div>

							</div>
						</div>
					) : !isLoadingNfts &&
					  blockchainNfts.length === 0 &&
					  userData!.walletAddress ? (
						/* No blockchain NFTs found, show database fallback */
						gameIdNftData && (
							<div className='mt-3 p-3 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border border-green-200 dark:border-green-800'>
								<div className='flex items-center gap-2 mb-2'>
									<CheckCircle className='h-4 w-4 text-green-600 dark:text-green-400' />
									<span className='font-medium text-green-900 dark:text-green-100 text-sm'>
										{nftStatus === 'minted'
											? 'NFT Minted Successfully'
											: 'NFT Minting in Progress'}
									</span>
								</div>
								<div className='space-y-1 text-xs text-green-800 dark:text-green-200'>
									{gameIdNftData.transactionHash && (
										<div className='flex items-center gap-1'>
											<span>Transaction:</span>
											<a
												href={`https://polygonscan.com/tx/${gameIdNftData.transactionHash}`}
												target='_blank'
												rel='noopener noreferrer'
												className='flex items-center gap-1 text-green-700 dark:text-green-300 hover:underline'
											>
												{gameIdNftData.transactionHash.slice(
													0,
													10
												)}
												...
												<ExternalLink className='h-3 w-3' />
											</a>
										</div>
									)}
									{gameIdNftData.tokenId &&
										gameIdNftData.tokenId !== 'pending' && (
											<div>
												Token ID:{' '}
												{gameIdNftData.tokenId}
											</div>
										)}
									{gameIdNftData.contractAddress &&
										gameIdNftData.contractAddress !==
											'pending' && (
											<div className='flex items-center gap-1'>
												<span>Contract:</span>
												<a
													href={`https://polygonscan.com/address/${gameIdNftData.contractAddress}`}
													target='_blank'
													rel='noopener noreferrer'
													className='flex items-center gap-1 text-green-700 dark:text-green-300 hover:underline'
												>
													{gameIdNftData.contractAddress.slice(
														0,
														10
													)}
													...
													<ExternalLink className='h-3 w-3' />
												</a>
											</div>
										)}
								</div>
								<p className='text-xs text-green-700 dark:text-green-300 mt-2'>
									No blockchain data found. This may be a
									recent mint still processing.
								</p>
							</div>
						)
					) : null}
				</div>
			);
		} else if (resolvedGameId) {
			// User has Game ID but no NFT data (old system or loading)
			return (
				<div className='space-y-2'>
					<div className='flex items-center justify-between'>
						<span className='font-medium text-lg text-foreground/80 dark:text-white/80'>
							{resolvedGameId}
						</span>
						<Button
							variant='ghost'
							size='sm'
							className='p-1 text-primary hover:text-primary/80 dark:text-pink-300 dark:hover:text-pink-200'
							onClick={() =>
								copyToClipboard(
									`https://${resolvedGameId}.watchersociety.com`,
									'Affiliate Link'
								)
							}
						>
							<Copy className='h-4 w-4' />
						</Button>
					</div>
					<p className='text-sm text-muted-foreground'>
						{resolvedGameId}.watchersociety.com
					</p>
					{/* {isOwnProfile && (
						<Button
							variant='outline'
							size='sm'
							onClick={() =>
								setShowAffiliateStats(!showAffiliateStats)
							}
							className='mt-2'
						>
							<Trophy className='mr-2 h-4 w-4' />
							{showAffiliateStats ? 'Hide' : 'Show'} Stats
						</Button>
					)} */}
				</div>
			);
		} else {
			// No Game ID
			return (
				<div className='text-orange-600 dark:text-orange-400 w-full aspect-square text-center relative rounded-xl overflow-hidden'>
					{isOwnProfile && (
						<Button
							asChild
							className='group relative z-10 w-full h-full border-2 border-dashed border-orange-600 dark:border-orange-600 rounded-xl bg-black/20 hover:bg-black/30 transition-all duration-300 overflow-hidden'
							variant='outline'
							size='sm'
						>
							<a href={WATCHER_HQ_MEMBER_PROFILE_URL}>
								{/* Background image inside button */}
								<img
									// no-public-ipfs-fallback: owner-authored Game ID background art on the link placeholder
									src='https://watchers.sirv.com/watchers/nft/gameid-bg.jpg'
									alt='Game ID background'
									className='absolute inset-0 w-full h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300'
								/>

								{/* Light overlay for text readability */}
								<div className='absolute inset-0 bg-black/10'></div>

								<div className='relative z-10 flex items-center justify-center'>
									<div className='w-full'>
										<div className='bg-black/60 px-8 py-6 rounded-xl'>
											<div className='flex items-center justify-center gap-2'>
												<Trophy className='h-10 w-10 dark:text-pink-600' />
												<div className='text-3xl font-semibold bg-gradient-to-r from-pink-500 to-purple-500 text-transparent bg-clip-text'>
													CLAIM GAME ID
												</div>
											</div>
										</div>
									</div>
								</div>
							</a>
						</Button>
					)}
				</div>
			);
		}
	};

	return (
		<div className='container mx-auto p-6 mt-0'>
			{/* Header */}
			<div className='mb-6'>
				<Link
					to='/admin'
					className='inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-4'
				>
					<ArrowLeft className='h-4 w-4 mr-2' />
					Dashboard
				</Link>
			</div>

			<div className='grid grid-cols-1 lg:grid-cols-[60%_40%] gap-6 max-w-[1200px] mx-auto'>
				<div>
					<Card className='bg-black/40 backdrop-blur-md border border-white/10 shadow-[0_0_30px_rgba(236,72,153,0.6)] rounded-3xl'>
						<CardContent className='p-6'>
							<div className='space-y-6'>
								{/* User Info */}
								<div className='w-full'>
									<div className='w-full'>
										<div className='flex items-start gap-6'>
											<div
												className='relative w-48 h-72 bg-primary/10 rounded-xl flex items-center justify-center border-2 border-primary/20 dark:border-pink-400/80 overflow-hidden flex-shrink-0 shadow-[0_0_15px_rgba(236,72,153,0.5)] dark:shadow-[0_0_15px_rgba(236,72,153,0.7)] cursor-pointer hover:shadow-[0_0_20px_rgba(236,72,153,0.7)] dark:hover:shadow-[0_0_20px_rgba(236,72,153,0.9)] transition-shadow duration-300'
												onClick={() =>
													accessPassNfts.length > 0 &&
													setShowAccessPassModal(true)
												}
												>
													{isLoadingAccessPass ? (
														<div className='absolute inset-0 flex items-center justify-center'>
															<img
																src={DEFAULT_ACCESS_PASS_IMAGE}
																alt=''
																aria-hidden='true'
																className='absolute inset-0 h-full w-full object-cover'
															/>
															<div className='absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]'>
																<Loader className='scale-50' message='' />
															</div>
														</div>
												) : currentAccessPassNft ? (
														<>
															<div
																className='absolute inset-0'
																style={{
																	opacity:
																		accessPassFadeState ===
																		'out'
																			? 0
																			: 1,
																	transition:
																		'opacity 0.15s ease-in-out',
																}}
															>
																<AccessPassNftImage
																	nft={currentAccessPassNft}
																/>
															</div>

															{accessPassNfts.length >
															1 && (
															<>
																<button
																	type='button'
																	onClick={(
																		e
																	) => {
																		e.stopPropagation();
																		handlePrevAccessPass();
																	}}
																	className='absolute left-2 top-1/2 -translate-y-1/2 bg-black/80 hover:bg-black/90 rounded-full p-1 text-white transition-colors'
																	aria-label='Previous access pass'
																>
																	<ArrowLeft className='h-4 w-4' />
																</button>
																<button
																	type='button'
																	onClick={(
																		e
																	) => {
																		e.stopPropagation();
																		handleNextAccessPass();
																	}}
																	className='absolute right-2 top-1/2 -translate-y-1/2 bg-black/80 hover:bg-black/90 rounded-full p-1 text-white transition-colors'
																	aria-label='Next access pass'
																>
																	<ArrowLeft className='h-4 w-4 rotate-180' />
																</button>
															</>
														)}
													</>
												) : (
													<User className='h-8 w-8 text-primary dark:text-pink-400' />
												)}
											</div>
											<div className='flex-1 space-y-4'>
												<div className='flex items-center space-x-2'>
													{isNameEditable ? (
														<div className='flex items-center space-x-2'>
															<Input
																ref={inputRef}
																value={
																	editedName
																}
																onChange={(e) =>
																	setEditedName(
																		e.target
																			.value
																	)
																}
																onKeyDown={
																	handleKeyPress
																}
																onBlur={
																	saveNameChange
																}
																className='text-xl font-semibold'
																maxLength={50}
															/>
														</div>
													) : (
														<div
															className='flex items-center space-x-6 group cursor-pointer'
															onClick={
																isOwnProfile
																	? startEditing
																	: undefined
															}
														>
															<h2 className='text-3xl font-semibold text-pink-600'>
																{userData.name}
															</h2>
															<SquarePenIcon className='h-2 w-2 text-muted-foreground' />
															{userData.role && (
																<Badge
																	variant={
																		userData.role ===
																		'admin'
																			? 'destructive'
																			: 'secondary'
																	}
																	className='bg-orange-600'
																>
																	{
																		userData.role
																	}
																</Badge>
															)}
														</div>
													)}
												</div>
												<p className='text-white/90'>
													{userData.email}
												</p>

												{/* Access Pass NFT Attributes */}
												<div className='mt-6'>
													{isLoadingAccessPass ? (
														<div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
															{Array.from({
																length: 4,
															}).map(
																(_, index) => (
																	<div
																		key={`access-pass-loading-${index}`}
																		className='p-2 sm:p-3 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-center shadow-[0_0_15px_rgba(236,72,153,0.2)] h-14 sm:h-16 flex items-center justify-center'
																	>
																		<div className='animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-pink-500/30 border-b-pink-500' />
																	</div>
																)
															)}
														</div>
												) : currentAccessPassNft?.metadata
														.attributes &&
												  currentAccessPassNft.metadata
														.attributes.length > 0 ? (
													<div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
														{currentAccessPassNft.metadata.attributes
															.filter(
																(attr) =>
																		attr.trait_type !==
																			'TBA Wallet' &&
																		attr.trait_type !==
																			'Type'
																)
																.slice(0, 4)
																.map(
																	(
																		attr,
																		attrIndex
																	) => (
																		<div
																			key={
																				attrIndex
																			}
																			className='p-2 sm:p-3 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-center shadow-[0_0_15px_rgba(236,72,153,0.2)] min-h-[3.5rem] sm:min-h-[4rem]'
																		>
																			<div className='text-xs sm:text-sm text-pink-300 uppercase tracking-wide mb-1 break-words'>
																				{attr.trait_type ===
																				'DAO Weight Multiplier'
																					? 'DAO Weight'
																					: attr.trait_type}
																			</div>
																			<div className='text-sm sm:text-lg font-medium text-white break-words [overflow-wrap:anywhere]'>
																				{attr.display_type ===
																				'boolean'
																					? attr.value ===
																						true
																						? 'Yes'
																						: 'No'
																					: String(
																							attr.value
																						)}
																			</div>
																		</div>
																	)
																)}
															{Array.from({
																length: Math.max(
																	0,
																	4 -
																		currentAccessPassNft.metadata.attributes.filter(
																			(
																				attr
																			) =>
																				attr.trait_type !==
																					'TBA Wallet' &&
																				attr.trait_type !==
																					'Type'
																		).length
																),
															}).map(
																(_, index) => (
																	<div
																		key={`access-pass-fill-${index}`}
																		className='p-2 sm:p-3 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-center shadow-[0_0_15px_rgba(236,72,153,0.2)] opacity-50 min-h-[3.5rem] sm:min-h-[4rem]'
																	>
																		<div className='text-xs sm:text-sm text-pink-300 uppercase tracking-wide mb-1'>
																			-
																		</div>
																		<div className='text-sm sm:text-lg font-medium text-white'>
																			-
																		</div>
																	</div>
																)
															)}
														</div>
													) : (
														<div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
															{Array.from({
																length: 4,
															}).map(
																(_, index) => (
																	<div
																		key={`access-pass-empty-${index}`}
																		className='p-2 sm:p-3 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-center shadow-[0_0_15px_rgba(236,72,153,0.2)] opacity-30 min-h-[3.5rem] sm:min-h-[4rem]'
																	>
																		<div className='text-xs sm:text-sm text-pink-300 uppercase tracking-wide mb-1'>
																			-
																		</div>
																		<div className='text-sm sm:text-lg font-medium text-white'>
																			-
																		</div>
																	</div>
																)
															)}
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* User Stats Grid */}
								<div className='grid grid-cols-1 gap-4'>
									{/* Game ID Section */}
									<div className='p-4 rounded-xl bg-black/60 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(236,72,153,0.2)]'>
										<div className='flex items-center my-2 ml-3'>
											<Trophy className='h-5 w-5 text-pink-600 mr-2' />
											<p className='text-xl font-semibold text-pink-600'>
												GAME ID
											</p>
											{sortedNfts.length > 1 &&
												isOwnProfile && (
													<div className='ml-4 text-sm text-white/60'>
														<span>
															({' '}
															{currentNftIndex +
																1}{' '}
															of{' '}
															{sortedNfts.length}{' '}
															)
														</span>
													</div>
												)}
										</div>
										<div className='w-full'>
											{renderGameIdSection()}
										</div>
									</div>

									<ProfileNftCollectionCard
										title='WATCHER BUILDER'
										icon={Hammer}
										nfts={watcherBuilderNfts}
										currentIndex={currentWatcherBuilderIndex}
										setCurrentIndex={setCurrentWatcherBuilderIndex}
										isLoading={isLoadingWatcherBuilder}
										error={watcherBuilderError}
										accentClass='text-orange-400'
										emptyMessage='WATCHER BUILDER NOT FOUND'
										copyToClipboard={copyToClipboard}
									/>

									<ProfileNftCollectionCard
										title='WATCHER PLAYER'
										icon={Gamepad2}
										nfts={watcherPlayerNfts}
										currentIndex={currentWatcherPlayerIndex}
										setCurrentIndex={setCurrentWatcherPlayerIndex}
										isLoading={isLoadingWatcherPlayer}
										error={watcherPlayerError}
										accentClass='text-purple-400'
										emptyMessage='WATCHER PLAYER NOT FOUND'
										copyToClipboard={copyToClipboard}
									/>

									{/* Access Rewards Section */}
									{accessRewards &&
										accessRewards.length > 0 && (
											<div className='w-full p-4 rounded-xl bg-card border border-border dark:bg-gradient-to-br dark:from-zinc-950/40 dark:to-zinc-900/40 dark:border-purple-900/30 dark:shadow-[0_0_15px_rgba(16,185,129,0.15)]'>
												<div className='flex items-center mb-4'>
													<Trophy className='h-5 w-5 text-pink-600 mr-2' />
													<p className='text-xl font-semibold text-pink-500 dark:text-pink-600'>
														ACCESS REWARDS
													</p>
													{accessRewards.length >
														1 && (
														<div className='ml-4 text-sm text-muted-foreground'>
															<span>
																(
																{currentAccessRewardIndex +
																	1}{' '}
																of{' '}
																{
																	accessRewards.length
																}
																)
															</span>
														</div>
													)}
												</div>

												{/* Access Rewards Carousel */}
												<div className='relative'>
													{/* Current Reward Display */}
													<div
														style={{
															opacity:
																accessRewardFadeState ===
																'out'
																	? 0
																	: 1,
															transition:
																'opacity 0.15s ease-in-out',
															willChange:
																'opacity',
														}}
													>
														{(() => {
															const reward =
																accessRewards[
																	currentAccessRewardIndex
																];
															if (!reward)
																return null;

															const rewardDate =
																new Date(
																	reward.claimedAt
																);
															const isCompleted =
																reward.transferStatus ===
																'completed';

															return (
																<div className='p-4 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 rounded-lg border border-pink-200 dark:border-pink-800'>
																	{/* Reward Header */}
																	<div className='flex items-center justify-between mb-3'>
																		<div className='flex items-center gap-2'>
																			<div className='text-2xl font-bold text-orange-600 dark:text-orange-400'>
																				{
																					reward.amount
																				}{' '}
																				$KARMA
																			</div>
																			{isCompleted ? (
																				<CheckCircle className='h-5 w-5 text-green-500' />
																			) : (
																				<div className='h-5 w-5 rounded-full border-2 border-orange-500 border-t-transparent animate-spin' />
																			)}
																		</div>
																		<div className='text-sm text-muted-foreground'>
																			{rewardDate.toLocaleDateString()}
																		</div>
																	</div>

																	{/* AI Character & Artwork */}
																	<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
																		{/* Artwork */}
																		{reward.artworkUrl && (
																			<div
																				className='relative cursor-pointer'
																				onMouseEnter={() =>
																					setHoveredReward(
																						reward
																					)
																				}
																				onMouseLeave={() =>
																					setHoveredReward(
																						null
																					)
																				}
																				onMouseMove={(
																					e
																				) => {
																					setMousePosition(
																						{
																							x: e.clientX,
																							y: e.clientY,
																						}
																					);
																				}}
																			>
																				<img
																					src={
																						reward.artworkUrl
																					}
																					alt={`${reward.aiName} artwork`}
																					className='w-full aspect-square object-cover rounded-lg border border-emerald-200 dark:border-emerald-700 transition-transform hover:scale-105'
																					onError={(
																						e
																					) => {
																						const target =
																							e.target as HTMLImageElement;
																						target.style.display =
																							'none';
																					}}
																				/>
																			</div>
																		)}

																		{/* Reward Details */}
																		<div className='space-y-3'>
																			<div>
																				<div className='text-sm font-semibold text-pink-700 dark:text-pink-500 mb-1'>
																					AI
																					CHARACTER
																				</div>
																				<div className='text-lg font-medium text-foreground dark:text-white'>
																					{
																						reward.aiName
																					}
																				</div>
																			</div>

																			<div>
																				<div className='text-sm font-semibold text-pink-700 dark:text-pink-500 mb-1'>
																					STATUS
																				</div>
																				<Badge
																					className={`${
																						isCompleted
																							? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
																							: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100'
																					}`}
																				>
																					{isCompleted
																						? 'Claimed'
																						: 'Processing'}
																				</Badge>
																			</div>

																			{reward.transactionHash && (
																				<div>
																					<div className='text-sm font-semibold text-pink-700 dark:text-pink-500 mb-1 pt-4'>
																						TRANSACTION
																					</div>
																					<div className='flex items-center gap-1'>
																						{/* Check if it's a real tx hash (starts with 0x) or still a queue ID */}
																						{reward.transactionHash.startsWith(
																							'0x'
																						) ? (
																							<a
																								href={`https://polygonscan.com/tx/${reward.transactionHash}`}
																								target='_blank'
																								rel='noopener noreferrer'
																								className='flex items-center gap-1 text-pink-700 dark:text-pink-300 hover:underline'
																							>
																								{reward.transactionHash.slice(
																									0,
																									10
																								)}
																								...
																								<ExternalLink className='h-3 w-3' />
																							</a>
																						) : (
																							<span className='text-sm text-muted-foreground'>
																								Processing...
																							</span>
																						)}
																					</div>
																				</div>
																			)}
																		</div>
																	</div>
																</div>
															);
														})()}
													</div>

													{/* Navigation Arrows - Only show if multiple rewards */}
													{accessRewards.length >
														1 && (
														<>
															{/* Left Arrow */}
															<div
																className='absolute left-2 top-1/2 transform -translate-y-1/2 z-10 hover:cursor-pointer bg-black/70 rounded-full p-2'
																onClick={
																	handlePrevAccessReward
																}
																style={{
																	filter: 'drop-shadow(0 0 12px rgba(16, 185, 129, 0.9)) drop-shadow(0 0 20px rgba(16, 185, 129, 0.6)) drop-shadow(0 0 30px rgba(16, 185, 129, 0.4))',
																}}
															>
																<ArrowLeft className='h-5 w-5 text-white' />
															</div>

															{/* Right Arrow */}
															<div
																className='absolute right-2 top-1/2 transform -translate-y-1/2 z-10 hover:cursor-pointer bg-black/70 rounded-full p-2'
																onClick={
																	handleNextAccessReward
																}
																style={{
																	filter: 'drop-shadow(0 0 12px rgba(16, 185, 129, 0.9)) drop-shadow(0 0 20px rgba(16, 185, 129, 0.6)) drop-shadow(0 0 30px rgba(16, 185, 129, 0.4))',
																}}
															>
																<ArrowLeft className='h-5 w-5 text-white rotate-180' />
															</div>
														</>
													)}
												</div>
											</div>
										)}
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				<div className='space-y-6'>
					{userData?.walletAddress && (
						<WalletCard className='bg-black/40 backdrop-blur-md border border-white/10 shadow-[0_0_30px_rgba(236,72,153,0.6)] rounded-3xl' />
					)}

					{userData?.walletAddress && (
						<TransactionHistory
							walletAddress={userData.walletAddress}
							className='bg-black/40 backdrop-blur-md border border-white/10 shadow-[0_0_30px_rgba(236,72,153,0.6)] rounded-3xl'
						/>
					)}

					{userData?.gameId && (
						<Card className='bg-black/40 backdrop-blur-md border border-white/10 shadow-[0_0_30px_rgba(236,72,153,0.6)] rounded-3xl'>
							<CardContent className='p-6'>
								<AffiliateStats gameId={userData.gameId} />
							</CardContent>
						</Card>
					)}
				</div>
			</div>

			{/* Access Pass NFT Full Size Modal */}
			{currentAccessPassNft && (
				<Dialog
					open={showAccessPassModal}
					onOpenChange={setShowAccessPassModal}
				>
					<DialogContent className='sm:max-w-4xl max-h-[90vh] bg-black border-2 border-pink-500/50 rounded-lg shadow-[0_0_30px_rgba(236,72,153,0.5)] p-4'>
						<DialogHeader className='my-3'>
							<div className='flex items-center justify-between'>
								<DialogTitle className='text-white text-center flex-1'>
									{currentAccessPassNft.metadata.name ||
										'Access Pass NFT'}
								</DialogTitle>
							</div>
						</DialogHeader>
						<div className='relative flex items-center justify-center'>
							{/* Current NFT Display */}
							<div
								className='h-[75vh] w-full max-w-full'
								style={{
									opacity:
										accessPassFadeState === 'out' ? 0 : 1,
									transition: 'opacity 0.15s ease-in-out',
									willChange: 'opacity',
								}}
							>
								<AccessPassNftImage
									nft={currentAccessPassNft}
									objectFit='contain'
									imageClassName='rounded-lg'
									fallbackIconClassName='h-16 w-16'
								/>
							</div>

							{/* Navigation Arrows - Only show if multiple NFTs */}
							{accessPassNfts.length > 1 && (
								<>
									{/* Left Arrow */}
									<div
										className='absolute left-4 top-1/2 transform -translate-y-1/2 z-10 hover:cursor-pointer bg-black/70 rounded-full p-3'
										onClick={handlePrevAccessPass}
										style={{
											filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.9)) drop-shadow(0 0 20px rgba(236, 72, 153, 0.6)) drop-shadow(0 0 30px rgba(236, 72, 153, 0.4))',
										}}
									>
										<ArrowLeft className='h-8 w-8 text-white' />
									</div>

									{/* Right Arrow */}
									<div
										className='absolute right-4 top-1/2 transform -translate-y-1/2 z-10 hover:cursor-pointer bg-black/70 rounded-full p-3'
										onClick={handleNextAccessPass}
										style={{
											filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.9)) drop-shadow(0 0 20px rgba(236, 72, 153, 0.6)) drop-shadow(0 0 30px rgba(236, 72, 153, 0.4))',
										}}
									>
										<ArrowLeft className='h-8 w-8 text-white rotate-180' />
									</div>
								</>
							)}
						</div>

						{/* NFT Details */}
						{/* {accessPassNfts[currentAccessPassIndex]?.metadata
							.attributes &&
							accessPassNfts[currentAccessPassIndex].metadata
								.attributes.length > 0 && (
								<div className='mt-6 text-white'>
									<h6 className='text-lg font-semibold mb-3 text-center'>
										NFT ATTRIBUTES
									</h6>
									<div className='grid grid-cols-2 gap-3 max-w-2xl mx-auto'>
										{accessPassNfts[
											currentAccessPassIndex
										].metadata.attributes
											.filter(
												(attr) =>
													attr.trait_type !==
													'TBA Wallet'
											)
											.map((attr, attrIndex) => (
												<div
													key={attrIndex}
													className='p-4 bg-white/10 border border-white/20 rounded-lg text-center'
												>
													<div className='text-sm text-white/70 uppercase tracking-wide mb-1'>
														{attr.trait_type ===
														'DAO Weight Multiplier'
															? 'DAO Weight'
															: attr.trait_type}
													</div>
													<div className='text-lg font-medium text-white break-all'>
														{attr.display_type ===
														'boolean'
															? attr.value ===
																true
																? '✓ Yes'
																: '✗ No'
															: String(
																	attr.value
																)}
													</div>
												</div>
											))}
									</div>
								</div>
							)} */}
						<div className='flex justify-center mt-[-20px]'>
							{accessPassNfts.length > 1 && (
								<div className='text-sm text-white/70'>
									{safeCurrentAccessPassIndex + 1} of{' '}
									{accessPassNfts.length}
								</div>
							)}
						</div>
					</DialogContent>
				</Dialog>
			)}

			{/* Access Reward Hover Tooltip */}
			{hoveredReward && (
				<AccessRewardTooltip
					reward={hoveredReward}
					mousePosition={mousePosition}
				/>
			)}
		</div>
	);
};
